import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPortfolioAllocation } from '../../api/metrics';
import CapitalAllocationDonut from '../charts/CapitalAllocationDonut';
import StandaloneSection from '../shared/StandaloneSection';
import './CapitalAllocationChart.css';

/**
 * Portfolio Holdings Chart Component
 * Displays portfolio breakdown by crypto asset and cash vs crypto comparison
 */
const PortfolioHoldingsChart: React.FC = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['portfolio', 'allocation'],
    queryFn: getPortfolioAllocation,
    retry: 2,
  });

  // Colors for crypto assets
  const CRYPTO_COLORS = [
    '#f7931a', // Bitcoin orange
    '#627eea', // Ethereum blue
    '#14f195', // Solana green
    '#8a8ced', // Purple
    '#06b6d4', // Cyan
    '#f59e0b', // Amber
    '#ec4899', // Pink
    '#10b981', // Emerald
  ];

  // Calculate crypto holdings by asset
  const getCryptoHoldingsByAsset = () => {
    if (!data?.allocations) return [];

    return data.allocations
      .filter(allocation => !allocation.isCash)
      .map((allocation, index) => ({
        name: allocation.asset,
        value: parseFloat(allocation.balanceFiat),
        color: CRYPTO_COLORS[index % CRYPTO_COLORS.length]
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10 assets
  };

  // Calculate cash vs crypto breakdown
  const getCashVsCrypto = () => {
    if (!data?.summary) return [];

    const totalCash = parseFloat(data.summary.totalCash);
    const totalCrypto = parseFloat(data.summary.totalCrypto);

    return [
      {
        name: 'Cash & Equivalents',
        value: totalCash,
        color: '#10b981' // Green
      },
      {
        name: 'Crypto Holdings',
        value: totalCrypto,
        color: '#8a8ced' // Purple
      }
    ].filter(item => item.value > 0);
  };

  if (isLoading) {
    return (
      <StandaloneSection title="PORTFOLIO HOLDINGS">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading holdings data...</p>
        </div>
      </StandaloneSection>
    );
  }

  if (isError || !data) {
    return (
      <StandaloneSection title="PORTFOLIO HOLDINGS">
        <div className="error-message">
          <p>Unable to load portfolio holdings data</p>
        </div>
      </StandaloneSection>
    );
  }

  const cryptoHoldings = getCryptoHoldingsByAsset();
  const cashVsCrypto = getCashVsCrypto();

  return (
    <StandaloneSection title="PORTFOLIO HOLDINGS">
      <div className="allocation-charts">
        <div className="allocation-section">
          <h3>Holdings by Asset (Top 10)</h3>
          {cryptoHoldings.length > 0 ? (
            <CapitalAllocationDonut data={cryptoHoldings} />
          ) : (
            <div className="no-data">No crypto holdings</div>
          )}
        </div>

        <div className="allocation-section">
          <h3>Cash vs Crypto</h3>
          {cashVsCrypto.length > 0 ? (
            <CapitalAllocationDonut data={cashVsCrypto} />
          ) : (
            <div className="no-data">No portfolio data</div>
          )}
        </div>
      </div>
    </StandaloneSection>
  );
};

export default PortfolioHoldingsChart;

